import { MedplumClient } from "@medplum/core";
import { Encounter, QuestionnaireResponse } from "@medplum/fhirtypes";

import zip from "lodash/zip";

import { fetchAmendmentHistory } from "@/encounters/services/questionnaire-response";

import { PhysicalExamSectionAnswer } from "../types/physical-exams";
import {
  fetchPhysicalExamHistoriesForDiff,
  getParentVersionId,
  resolvePhysicalExamVersionPair,
} from "../utils/physical-exam-diff";
import { getActivePhysicalExamsURLs, getSectionAnswers } from "../utils/physical-exams";

export interface PhysicalExamDiffResult {
  [title: string]: PhysicalExamSectionAnswer[][];
}

export async function getPhysicalExamDiffHistory(
  medplum: MedplumClient,
  encounter: Encounter,
  versionId?: string,
  currentVersionIndex?: number,
): Promise<PhysicalExamDiffResult> {
  const urls = getActivePhysicalExamsURLs(encounter);
  const encounterAmendmentHistory = versionId
    ? await fetchAmendmentHistory(medplum, encounter)
    : undefined;
  const encounterVersionIds =
    encounterAmendmentHistory
      ?.map((questionnaireResponse) => questionnaireResponse.meta?.versionId)
      .filter((id): id is string => Boolean(id)) ?? [];

  const histories = await urls.reduce(
    async (prevPromise, curr) => {
      const prev = await prevPromise;
      const { examHistories, fullResourceHistory, encounterPhysicalExamResponses, title } =
        await fetchPhysicalExamHistoriesForDiff(medplum, curr, encounter.id!);
      if (!title || !examHistories) return prev;

      let currentQuestionnaireResponse: QuestionnaireResponse | undefined;
      let previousQuestionnaireResponse: QuestionnaireResponse | undefined;

      if (versionId) {
        if (encounterVersionIds.length > 0) {
          ({ currentQuestionnaireResponse, previousQuestionnaireResponse } =
            resolvePhysicalExamVersionPair(
              examHistories,
              versionId,
              encounterVersionIds,
              fullResourceHistory,
              encounterPhysicalExamResponses,
            ));
        } else {
          const currentIndex = examHistories.findIndex(
            (questionnaireResponse) => getParentVersionId(questionnaireResponse) === versionId,
          );

          if (currentIndex === -1) {
            currentQuestionnaireResponse = examHistories[0];
            previousQuestionnaireResponse = examHistories[0];
          } else {
            currentQuestionnaireResponse = examHistories[currentIndex];
            previousQuestionnaireResponse = examHistories[currentIndex + 1];
          }
        }
      } else if (currentVersionIndex !== undefined) {
        const historyLength = examHistories?.length;
        if (historyLength < currentVersionIndex + 1) return prev;

        currentQuestionnaireResponse = examHistories[currentVersionIndex];
        previousQuestionnaireResponse =
          examHistories[currentVersionIndex + 1] ?? examHistories.at(-1);
      } else {
        return prev;
      }

      const currentAns = getSectionAnswers(currentQuestionnaireResponse);
      const previousAns = getSectionAnswers(previousQuestionnaireResponse);

      if (currentAns.length > 0 || previousAns.length > 0) {
        const zippedData = zip(currentAns, previousAns).filter(
          ([current, previous]) => current !== undefined || previous !== undefined,
        );

        if (zippedData.length > 0) {
          return {
            ...prev,
            [title]: zippedData,
          };
        }
      }

      return prev;
    },
    Promise.resolve({} as PhysicalExamDiffResult),
  );

  return histories;
}
